import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import OrderCustomerDetailsV2 from '~pages/Sales/Orders/NewOrderForm/OrderCustomerDetailsV2';

import {
  type OrderFormV3FieldValues,
  useOrderFormV3Context,
} from '../OrderFormV3Context';

/**
 * Renders `order.form.order_customer_details`. Render-only: the company
 * selector (create only — hidden once the order has an orderId) plus the
 * embedded OrderCustomerDetailsV2 exactly as legacy mounts it (no fork; its
 * internal show_contract_number company-setting read and effects ship
 * unchanged). Every non-form input arrives via context: no watch hooks, no
 * flag reads, no effects in this file.
 */
export function OrderCustomerDetailsPanel(): JSX.Element {
  const { control, formState } = useFormContext<OrderFormV3FieldValues>();
  const {
    defaultOrder,
    flags,
    isCopyAndEdit,
    isServiceClassRequired,
    projectData,
    shouldRequireOrderName,
    showServiceClass,
    tomlinsonOrderFormProjectFirst,
    watchedCompany,
  } = useOrderFormV3Context();

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('order.form.order_customer_details')}
      </Typography>

      {!defaultOrder?.orderId && (
        <Box data-test-id="order-form-v3-company" sx={{ mb: 2.5 }}>
          <CompanySelectorFormField
            control={control}
            defaultValue={defaultOrder?.company}
            errors={formState.errors}
            isRequired
            name="company"
          />
        </Box>
      )}

      <OrderCustomerDetailsV2
        companyId={watchedCompany?.id}
        isCopyAndEdit={isCopyAndEdit}
        isOrderNameRequired={shouldRequireOrderName}
        isServiceClassFieldVisible={showServiceClass}
        isServiceClassRequired={isServiceClassRequired}
        project={projectData ?? undefined}
        tomlinsonOrderFormProjectFirstFeatureFlagEnabled={tomlinsonOrderFormProjectFirst}
        tomlinsonOrderModalFeatureFlagEnabled={flags.tomlinsonOrderModal}
      />
    </Card>
  );
}
